import React, { ReactNode } from 'react';
import { View, Text, StyleSheet, Image } from 'react-native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { Event } from '@/types/residents/event';
import {
  getEventBadgeInfo,
  getEventCapacityDisplay,
  EventBadgeInfo,
} from '@/utils/residents/events/eventHelpers';

interface Props {
  event: Event;
  dateText: string;
  timeRange: string;
  imageUrl?: string;
  descriptionNode?: ReactNode;
  onAccept?: () => void;
  onDecline?: () => void;
  actions: ReactNode;
}

export const DesktopEventCardBase: React.FC<Props> = ({ event, dateText, timeRange, imageUrl, descriptionNode, actions }) => {
  // Use shared badge helper
  const badgeInfo = getEventBadgeInfo(event);
  const capacityDisplay = getEventCapacityDisplay(event);

  // Map badge style to desktop variant
  const getBadgeVariant = (info: EventBadgeInfo | null): 'neutral' | 'attention' | 'success' => {
    if (!info) return 'neutral';
    switch (info.style) {
      case 'attending': return 'success';
      case 'gradient': return 'attention';
      case 'full': return 'neutral';
      default: return 'neutral';
    }
  };

  return (
    <View style={styles.card} testID="desktop-event-card">
      <View style={styles.photoSection}>
        <View style={styles.photoContainer}>
          {imageUrl ? (
            <Image source={{ uri: imageUrl }} style={styles.photo} resizeMode="cover" />
          ) : (
            <RESIDENTS_SVG_COMPONENTS.PROPERTY_PLACEHOLDER width={220} height={180} />
          )}
        </View>
      </View>

      <View style={styles.contentSection}>
        <View style={styles.contentRow}>
          <View style={styles.infoCol}>
            <View style={styles.badgesRow}>
              {badgeInfo && (
                <Badge 
                  label={badgeInfo.text} 
                  variant={getBadgeVariant(badgeInfo)} 
                  showCheckmark={badgeInfo.showCheckmark}
                />
              )}
            </View>

            {!!event?.name && <Text style={styles.title} numberOfLines={2}>{event.name}</Text>}

            <View style={styles.detailsGrid}>
              <View style={styles.inlineRow}>
                <View style={styles.inlineCol}>
                  {!!dateText ? (
                    <DetailRow Icon={RESIDENTS_SVG_COMPONENTS.CALENDAR} text={dateText} />
                  ) : <View />}
                </View>
                <View style={styles.inlineCol}>
                  {!!event?.location ? (
                    <DetailRow Icon={RESIDENTS_SVG_COMPONENTS.LOCATION} text={event.location} />
                  ) : <View />}
                </View>
              </View>

              <View style={styles.inlineRow}>
                <View style={styles.inlineCol}>
                  {!!timeRange ? (
                    <DetailRow Icon={RESIDENTS_SVG_COMPONENTS.CLOCK} text={timeRange} />
                  ) : <View />}
                </View>
                <View style={styles.inlineCol}>
                  <DetailRow Icon={RESIDENTS_SVG_COMPONENTS.GROUP} text={capacityDisplay} />
                </View>
              </View>
            </View>

            <View style={styles.descriptionRow}>{descriptionNode}</View>
          </View>

          <View style={styles.actionsCol}>{actions}</View>
        </View>
      </View>
    </View>
  );
};

export const Badge: React.FC<{ 
  label: string; 
  variant: 'neutral' | 'attention' | 'success';
  showCheckmark?: boolean;
}> = ({ label, variant, showCheckmark }) => {
  return (
    <View style={[styles.badge, variant === 'success' && styles.badgeSuccess, variant === 'attention' && styles.badgeAttention]}>
      {(variant === 'success' || showCheckmark) && (
        <RESIDENTS_SVG_COMPONENTS.CHECK width={16} height={16} color={RESIDENTS_DESKTOP_COLORS.TEXT_SUCCESS} />
      )}
      <Text style={[
        styles.badgeText,
        variant === 'success' && styles.badgeTextSuccess,
        variant === 'attention' && styles.badgeTextAttention,
      ]}>{label}</Text>
    </View>
  );
};

export const DetailRow: React.FC<{ Icon: any; text: string }> = ({ Icon, text }) => (
  <View style={styles.detailRow}>
    <Icon width={20} height={20} color={RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY} />
    <Text style={styles.detailText} numberOfLines={1}>{text}</Text>
  </View>
);

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
    borderRadius: 16,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.06)',
  },
  photoSection: {
    paddingTop: 8,
    paddingLeft: 8,
    paddingBottom: 8,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
  },
  photoContainer: {
    width: 220,
    borderRadius: 16,
    overflow: 'hidden',
  },
  photo: {
    width: '100%',
    height: 180,
  },
  contentSection: {
    flex: 1,
    padding: 16,
    gap: 8,
  },
  contentRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
  },
  infoCol: {
    flex: 1,
    minWidth: 0,
  },
  actionsCol: {
    width: 220,
    alignItems: 'flex-end',
    gap: 8,
  },
  badgesRow: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
    paddingTop: 2,
    marginBottom: 8,
  },
  badge: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.06)',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  badgeAttention: {
    backgroundColor: 'rgba(7,62,236,0.12)',
  },
  badgeSuccess: {
    backgroundColor: 'rgba(16,185,129,0.15)',
  },
  badgeText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontSize: 12,
  },
  badgeTextSuccess: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_SUCCESS,
  },
  badgeTextAttention: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_SECONDARY,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  detailsGrid: {
    marginTop: 6,
    rowGap: 6,
  },
  inlineRow: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'center',
    flexWrap: 'nowrap',
  },
  inlineCol: {
    flex: 1,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  detailText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontSize: 14,
    flex: 1,
  },
  descriptionRow: {},
});

export default DesktopEventCardBase;


